import React, { Component, createRef } from "react";
import { createPortal } from "react-dom";
import { injectIntl, type IntlShape } from "react-intl";
import NotificationSystem from "react-notification-system";
import { connect, type ConnectedProps } from "react-redux";
import { toast as sonnerToast, type ToasterProps } from "sonner";

import { eraseUserActionNotification } from "~/actions/user_notification";
import { featureFlags } from "~/common/feature-flags";
import { remoteLog } from "~/common/logging";
import { RedesignPopupDismissProvider } from "~/components/notifications/patient-action-notification";
import type { TUserActionNotification } from "~/reducers/dashboard";
import type { RootState } from "~/store";

const mapStateToProps = (state: RootState) => ({
  userActionNotification: state.userActionNotification,
});

const mapDispatchToProps = {
  eraseUserActionNotification,
};

const style = {
  Dismiss: {
    DefaultStyle: {
      display: "none",
    },
  },
  NotificationItem: {
    success: {
      borderTop: "2px solid " + "#96dfdb",
      backgroundColor: "#e9ecf3",
      color: "#007a7f",
      WebkitBoxShadow: "0 0 1px  rgba(150,223,219,0.9)",
      MozBoxShadow: "0 0 1px  rgba(150,223,219,0.9)",
      boxShadow: "0 0 1px rgba(150,223,219,0.9)",
    },
    info: {
      borderTop: "2px solid " + "#FFFFFF",
      backgroundColor: "#FFFFFF",
      color: "#007a7f",
      WebkitBoxShadow: "0 0 1px  rgba(150,223,219,0.9)",
      MozBoxShadow: "0 0 1px  rgba(150,223,219,0.9)",
      boxShadow: "0 0 1px rgba(150,223,219,0.9)",
    },
  },
} satisfies NotificationSystem.Style;

type NotificationProps = PropsFromRedux & { intl: IntlShape } & {
  style?: NotificationSystem.Style;
};

const SONNER_POSITION: Record<NonNullable<TUserActionNotification["position"]>, ToasterProps["position"]> = {
  tl: "top-left",
  tr: "top-right",
  tc: "top-center",
  bl: "bottom-left",
  br: "bottom-right",
  bc: "bottom-center",
};

type NotificationState = {
  /** Redesign popup content rendered outside NotificationSystem (no green host). */
  redesignPopup: React.ReactNode | null;
};

// Only show a notification when userActionNotification actually changes (prevents duplicates).
class Notification extends Component<NotificationProps, NotificationState> {
  notificationSystemRef = createRef<NotificationSystem>();
  redesignAutoDismissTimer: number | null = null;
  state: NotificationState = { redesignPopup: null };

  componentDidCatch(e: Error) {
    remoteLog(e, "notification");
  }

  componentWillUnmount() {
    this.clearRedesignAutoDismiss();
  }

  clearRedesignAutoDismiss() {
    if (this.redesignAutoDismissTimer != null) {
      window.clearTimeout(this.redesignAutoDismissTimer);
      this.redesignAutoDismissTimer = null;
    }
  }

  componentDidMount() {
    if (this.props.userActionNotification) {
      this.addNotification(this.props.userActionNotification);
      this.props.eraseUserActionNotification();
    }
  }

  componentDidUpdate(prevProps: NotificationProps) {
    if (
      this.props.userActionNotification &&
      this.props.userActionNotification !== prevProps.userActionNotification
    ) {
      this.addNotification(this.props.userActionNotification);
      this.props.eraseUserActionNotification();
    }
  }

  addNotification(notification: TUserActionNotification) {
    // Under redesign, popup notifications with `children` must NOT go through
    // react-notification-system — its success chrome (green bar / ×) always leaks
    // around `visibility: visible` children. Render the card in a plain portal instead.
    if (featureFlags.redesignV1 && notification.children != null) {
      this.clearRedesignAutoDismiss();
      this.setState({ redesignPopup: notification.children });
      const autoDismissSec = notification.autoDismiss ?? 0;
      if (autoDismissSec > 0) {
        this.redesignAutoDismissTimer = window.setTimeout(() => {
          this.dismissRedesignPopup();
        }, autoDismissSec * 1000);
      }
      return;
    }

    if (notification.children != null) {
      this.notificationSystemRef.current?.addNotification({ ...notification, message: "", dismissible: false });
      return;
    }

    const message = this.props.intl.formatMessage({ id: notification.message }, { ...notification.values });
    const sonnerLevel = notification.level === "success" || notification.level === "error" || notification.level === "warning" || notification.level === "info" ? notification.level : "message";
    const duration = notification.autoDismiss == null ? 6000 : notification.autoDismiss > 0 ? notification.autoDismiss * 1000 : Infinity;
    sonnerToast[sonnerLevel](message, {
      duration,
      dismissible: true,
      position: notification.position ? SONNER_POSITION[notification.position] : undefined,
    });
  }

  dismissRedesignPopup = () => {
    this.clearRedesignAutoDismiss();
    this.setState({ redesignPopup: null });
  };

  render() {
    const { redesignPopup } = this.state;

    return (
      <>
        <NotificationSystem ref={this.notificationSystemRef} style={this.props.style ?? style} />
        {redesignPopup != null
          ? createPortal(
              <div
                data-redesign-v1
                data-testid="rd-notification-portal"
                // Match the 60% backdrop used by AlertDialog and CenteredDialog.
                className="tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-items-center tw-justify-center tw-bg-backdrop tw-p-4"
                onClick={(event) => {
                  if (event.target === event.currentTarget) this.dismissRedesignPopup();
                }}
                onKeyDown={(event) => {
                  if (event.key === "Escape") this.dismissRedesignPopup();
                }}
                role="presentation"
              >
                <div role="dialog" aria-modal="true">
                  <RedesignPopupDismissProvider dismiss={this.dismissRedesignPopup}>
                    {redesignPopup}
                  </RedesignPopupDismissProvider>
                </div>
              </div>,
              document.body,
            )
          : null}
      </>
    );
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(injectIntl(Notification));
